// Sprint-105a · per-parcel photo upload tile for MultiItemEditor.
// Reads a File via the hidden <input>, converts to base64 data URI inline
// (matches the project's existing photo-storage architecture — no server
// upload endpoint is hit; the data URI is sent inside the order create
// payload alongside dimensions).
import { useRef } from "react";
import { Camera, X, Loader2 } from "lucide-react";

const MAX_BYTES = 2_500_000;   // matches LineItem.customer_package_photo max_length

export default function ParcelPhotoUpload({
  value,           // base64 data URI string or null
  onChange,        // (dataUriOrNull) => void
  onDetect,        // optional () => void
  detecting = false,
  parcelIndex,
}) {
  const inputRef = useRef(null);

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      alert("Photo too large — please pick one under 2.5 MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const v = typeof reader.result === "string" ? reader.result : null;
      onChange(v);
    };
    reader.readAsDataURL(file);
    e.target.value = "";   // allow re-pick the same file later
  };

  const removed = () => onChange(null);

  return (
    <div className="flex items-start gap-2" data-testid={`parcel-photo-${parcelIndex}`}>
      {value ? (
        <div className="relative shrink-0">
          <img
            src={value}
            alt={`Parcel ${parcelIndex + 1} preview`}
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-slate-200"
            data-testid={`parcel-photo-thumb-${parcelIndex}`}
          />
          <button
            type="button"
            onClick={removed}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-200 shadow text-slate-500 hover:text-rose-600 flex items-center justify-center"
            aria-label="Remove photo"
            data-testid={`parcel-photo-remove-${parcelIndex}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/40 text-orange-600 flex flex-col items-center justify-center hover:bg-orange-50 transition shrink-0"
          data-testid={`parcel-photo-upload-${parcelIndex}`}
        >
          <Camera className="h-5 w-5 mb-0.5" />
          <span className="text-[10px] font-semibold uppercase tracking-wider">+ Photo</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePick}
        data-testid={`parcel-photo-input-${parcelIndex}`}
      />
      {value && onDetect && (
        <button
          type="button"
          onClick={onDetect}
          disabled={detecting}
          className="self-start mt-1 text-[11px] font-semibold text-orange-600 hover:text-orange-700 disabled:text-slate-400 inline-flex items-center gap-1"
          data-testid={`parcel-photo-detect-${parcelIndex}`}
        >
          {detecting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          Detect Size
        </button>
      )}
    </div>
  );
}
